import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Megaphone, Film, GraduationCap, ArrowLeft, Star,
  ChevronLeft, ChevronRight, Quote, Briefcase,
  Lightbulb, Rocket, Target, TrendingUp, Play, Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/contexts/SettingsContext";
import { FeaturedInfluencersSection } from "@/pages/InfluencersPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Reveal({ children, className = "", delay = 0, direction = "up" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const dirs = {
    up: { hidden: { opacity: 0, y: 60 }, show: { opacity: 1, y: 0 } },
    down: { hidden: { opacity: 0, y: -60 }, show: { opacity: 1, y: 0 } },
    left: { hidden: { opacity: 0, x: -60 }, show: { opacity: 1, x: 0 } },
    right: { hidden: { opacity: 0, x: 60 }, show: { opacity: 1, x: 0 } },
    scale: { hidden: { opacity: 0, scale: 0.85 }, show: { opacity: 1, scale: 1 } },
  };
  return (
    <motion.div
      ref={ref}
      initial={dirs[direction].hidden}
      animate={inView ? dirs[direction].show : dirs[direction].hidden}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >{children}</motion.div>
  );
}

// Counter animation hook
function useCountUp(end, inView, duration = 2000) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const inc = end / (duration / 16);
    const timer = setInterval(() => {
      start += inc;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [end, inView, duration]);
  return count;
}

// ===== HERO =====
function HeroSection() {
  const settings = useSettings();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 200]);
  const textY = useTransform(scrollY, [0, 500], [0, 100]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background with parallax */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <div className="absolute inset-0 bg-[#0a0a0b]" />
        <img
          src={settings.hero_image || "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"}
          alt=""
          className="w-full h-full object-cover opacity-[0.12]"
        />
      </motion.div>

      {/* Grid overlay */}
      <div className="absolute inset-0 bg-grid opacity-50" />
      
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0b]/30 to-[#0a0a0b]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b]/50 via-transparent to-[#0a0a0b]/50" />

      {/* Floating shapes - desktop only */}
      <div className="hidden md:block absolute top-[15%] right-[10%] w-72 h-72 border border-primary/10 rounded-full animate-spin-slow" />
      <div className="hidden md:block absolute top-[20%] right-[12%] w-56 h-56 border border-primary/5 rounded-full animate-spin-slow" style={{ animationDirection: "reverse", animationDuration: "30s" }} />
      <div className="hidden md:block absolute bottom-[20%] left-[8%] w-40 h-40 bg-primary/5 rounded-3xl rotate-45 animate-float-slow" />
      <div className="absolute top-[30%] left-[15%] w-2 h-2 bg-primary/40 rounded-full animate-float" />
      <div className="absolute top-[50%] right-[20%] w-2 h-2 bg-primary/30 rounded-full animate-float" style={{ animationDelay: "1s" }} />
      <div className="absolute bottom-[35%] left-[30%] w-1.5 h-1.5 bg-primary/20 rounded-full animate-float" style={{ animationDelay: "2s" }} />

      {/* Main glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-primary/8 rounded-full blur-[150px]" />

      {/* Content */}
      <motion.div className="relative z-10 container mx-auto px-4 md:px-6 pt-32 md:pt-28" style={{ y: textY, opacity }}>
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex items-center gap-3 mb-6 md:mb-8"
          >
            <div className="h-[1px] w-12 bg-primary" />
            <span className="text-primary text-xs md:text-sm font-semibold tracking-wider">
              تسويق &middot; إنتاج &middot; تدريب &middot; توظيف
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl sm:text-5xl lg:text-8xl font-black font-['Zain'] text-white leading-[1.3] mb-6 md:mb-8"
          >
            <span className="block mb-2 md:mb-4">نصنع</span>
            <span className="block text-gradient">محتوى يمثلنا</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-sm md:text-lg text-zinc-400 max-w-xl leading-relaxed mb-8 md:mb-12"
          >
            نحوّل الأفكار إلى تجارب مؤثرة من خلال حلول إبداعية متكاملة
            في التسويق الرقمي، الإنتاج المرئي، التدريب الاحترافي، والتوظيف.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex flex-col sm:flex-row gap-3 md:gap-4"
          >
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-primary hover:bg-[#E54D2E] text-white px-8 md:px-10 py-5 md:py-6 text-sm md:text-base rounded-xl red-glow transition-all group w-full sm:w-auto"
                data-testid="hero-cta-contact"
              >
                ابدأ مشروعك الآن
                <ArrowLeft size={18} className="ms-2 group-hover:-translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button
                size="lg"
                variant="outline"
                className="border-white/10 bg-white/[0.03] text-white hover:bg-white/5 px-8 md:px-10 py-5 md:py-6 text-sm md:text-base rounded-xl backdrop-blur-sm w-full sm:w-auto"
                data-testid="hero-cta-portfolio"
              >
                <Play size={16} className="me-2" />
                شاهد أعمالنا
              </Button>
            </Link>
          </motion.div>
        </div>

        {/* Hero stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="flex gap-6 md:gap-14 mt-12 md:mt-20 pt-8 md:pt-10 border-t border-white/5"
        >
          {[
            { num: "+150", label: "مشروع منجز" },
            { num: "+50", label: "عميل سعيد" },
            { num: "+10", label: "سنوات خبرة" },
          ].map((s, i) => (
            <div key={i} className="text-right">
              <div className="text-xl md:text-3xl font-black font-['Zain'] text-white">{s.num}</div>
              <div className="text-zinc-500 text-[10px] md:text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2"
        >
          <div className="w-1 h-2 bg-primary rounded-full" />
        </motion.div>
      </div>
    </section>
  );
}

// ===== ABOUT STRIP =====
function AboutStrip() {
  return (
    <section className="py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-dots opacity-30" />
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal direction="right">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-[2px] w-10 bg-primary animate-line-grow" />
                <span className="text-primary text-sm font-semibold">من نحن</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-black font-['Zain'] text-white leading-tight mb-6">
                نبني مستقبل العلامات
                <br />
                <span className="text-gradient">التجارية</span>
              </h2>
              <p className="text-zinc-400 text-base leading-relaxed mb-8 max-w-lg">
                شركة رائدة تجمع بين الإبداع والاحترافية لتقديم حلول متكاملة. من التسويق الرقمي
                إلى الإنتاج المرئي والتدريب، نساعد العلامات التجارية على التألق والنمو.
              </p>
              <Link to="/about">
                <Button variant="outline" className="border-white/10 text-white hover:bg-white/5 rounded-xl px-6" data-testid="about-learn-more">
                  تعرّف علينا أكثر
                  <ArrowLeft size={14} className="ms-2" />
                </Button>
              </Link>
            </div>
          </Reveal>

          <Reveal direction="left" delay={0.2}>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Lightbulb, num: "إبداع", desc: "حلول مبتكرة" },
                { icon: Target, num: "دقة", desc: "استراتيجيات مدروسة" },
                { icon: TrendingUp, num: "نمو", desc: "نتائج ملموسة" },
                { icon: Users, num: "فريق", desc: "خبراء محترفون" },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={i}
                    whileHover={{ y: -4, scale: 1.02 }}
                    className="bg-[#131315] border border-white/5 rounded-2xl p-6 bento-glow transition-all"
                    data-testid={`about-card-${i}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <Icon size={20} className="text-primary" />
                    </div>
                    <div className="text-lg font-bold font-['Zain'] text-white">{item.num}</div>
                    <div className="text-zinc-500 text-xs">{item.desc}</div>
                  </motion.div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ===== SERVICES BENTO =====
function ServicesSection() {
  const services = [
    { icon: Megaphone, title: "التسويق", desc: "إدارة حسابات، صناعة محتوى، حملات إعلانية، هوية تجارية، واستراتيجيات تسويقية متكاملة", href: "/marketing", accent: "#CB360F" },
    { icon: Film, title: "الإنتاج", desc: "تصوير احترافي، إخراج، مونتاج، صناعة إعلانات، تغطيات إعلامية ومحتوى إبداعي", href: "/production", accent: "#E8590C" },
    { icon: GraduationCap, title: "التدريب", desc: "دورات متخصصة، ورش عمل، برامج تطوير مهني، تدريب عملي وشهادات حضور", href: "/training", accent: "#D97706" },
    { icon: Briefcase, title: "التوظيف", desc: "فرص عمل مميزة في بيئة إبداعية. انضم لفريقنا وطوّر مسيرتك المهنية", href: "/recruitment", accent: "#059669" },
  ];

  return (
    <section data-testid="services-section" className="py-20 md:py-28 bg-[#0a0a0b] relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-30" />
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 rounded-full blur-[150px]" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <Reveal className="mb-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-[2px] w-10 bg-primary" />
            <span className="text-primary text-sm font-semibold">أقسامنا</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black font-['Zain'] text-white leading-tight">
            أربعة أقسام
            <br className="hidden md:block" />
            <span className="text-gradient"> حلول لا محدودة</span>
          </h2>
        </Reveal>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            const isLarge = i === 0;
            return (
              <Reveal key={i} delay={i * 0.1}>
                <Link to={s.href} className="block group" data-testid={`service-card-${i}`}>
                  <div className={`relative bg-[#131315] border border-white/5 rounded-2xl overflow-hidden tilt-card bento-glow ${isLarge ? "md:row-span-2 min-h-[240px] md:min-h-[320px]" : "min-h-[200px]"} flex flex-col justify-end p-6 md:p-8`}>
                    {/* Background accent */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                      style={{ background: `radial-gradient(circle at top right, ${s.accent}10, transparent 70%)` }} />

                    {/* Floating number */}
                    <span className="absolute top-6 left-6 text-[80px] md:text-[100px] font-black font-['Zain'] leading-none text-white/[0.02] group-hover:text-white/[0.05] transition-all duration-700">
                      0{i + 1}
                    </span>

                    <div className="relative z-10">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110"
                        style={{ backgroundColor: `${s.accent}15` }}>
                        <Icon size={26} style={{ color: s.accent }} />
                      </div>
                      <h3 className="text-2xl font-bold font-['Zain'] text-white mb-3">{s.title}</h3>
                      <p className="text-zinc-400 text-sm leading-relaxed mb-5 max-w-sm">{s.desc}</p>
                      <span className="inline-flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all duration-300" style={{ color: s.accent }}>
                        اكتشف المزيد
                        <ArrowLeft size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===== STATS =====
function StatCard({ end, suffix, label, inView, delay }) {
  const count = useCountUp(end, inView);
  return (
    <Reveal delay={delay} direction="scale">
      <div className="text-center">
        <div className="text-4xl md:text-6xl font-black font-['Zain'] text-white mb-2">
          {count}{suffix}
        </div>
        <div className="text-zinc-500 text-sm">{label}</div>
      </div>
    </Reveal>
  );
}

function StatsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const stats = [
    { end: 150, suffix: "+", label: "مشروع منجز" },
    { end: 50, suffix: "+", label: "عميل سعيد" },
    { end: 30, suffix: "+", label: "دورة تدريبية" },
    { end: 10, suffix: "+", label: "سنوات خبرة" },
  ];

  return (
    <section ref={ref} className="py-16 relative">
      <div className="glow-line" />
      <div className="container mx-auto px-4 md:px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <StatCard key={i} end={s.end} suffix={s.suffix} label={s.label} inView={inView} delay={i * 0.1} />
          ))}
        </div>
      </div>
      <div className="glow-line" />
    </section>
  );
}

// ===== PORTFOLIO =====
function PortfolioPreview({ items }) {
  const [playingVideo, setPlayingVideo] = useState(null);
  const getYtId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : null;
  };
  return (
    <section data-testid="portfolio-preview" className="py-20 md:py-28 bg-[#0a0a0b] relative overflow-hidden">
      <div className="absolute inset-0 bg-dots opacity-20" />
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <Reveal className="flex flex-col md:flex-row items-start md:items-end justify-between mb-14 gap-4">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-[2px] w-10 bg-primary" />
              <span className="text-primary text-sm font-semibold">أعمالنا</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-black font-['Zain'] text-white">
              مشاريع <span className="text-gradient">نفخر بها</span>
            </h2>
          </div>
          <Link to="/portfolio">
            <Button variant="outline" className="border-white/10 text-white hover:bg-white/5 rounded-xl" data-testid="portfolio-view-all">
              عرض الكل
              <ArrowLeft size={14} className="ms-2" />
            </Button>
          </Link>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.slice(0, 6).map((item, i) => {
            const ytId = getYtId(item.video_url);
            return (
            <Reveal key={item.id} delay={i * 0.08}>
              <motion.div
                whileHover={{ y: -6 }}
                className="group relative rounded-2xl overflow-hidden bg-[#131315] border border-white/5 bento-glow"
                data-testid={`portfolio-item-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  {playingVideo === item.id && ytId ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
                      className="w-full h-full"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                      title={item.title}
                    />
                  ) : (
                    <>
                      <img
                        src={ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1.2s] ease-out"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/20 to-transparent" />
                      {ytId && (
                        <button
                          onClick={() => setPlayingVideo(item.id)}
                          className="absolute inset-0 flex items-center justify-center z-10 cursor-pointer"
                          data-testid={`home-play-video-${i}`}
                        >
                          <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center shadow-lg shadow-primary/30 hover:scale-110 transition-transform">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                          </div>
                        </button>
                      )}
                    </>
                  )}
                  {playingVideo === item.id && (
                    <button
                      onClick={() => setPlayingVideo(null)}
                      className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                  )}
                </div>
                <div className="absolute bottom-0 right-0 left-0 p-6">
                  <span className="inline-block px-2.5 py-0.5 rounded-md bg-primary/15 text-primary text-xs font-medium mb-2">
                    {item.category === "marketing" ? "تسويق" : item.category === "production" ? "إنتاج" : item.category === "ads" ? "إعلانات" : "تدريب"}
                  </span>
                  <h4 className="text-white font-bold font-['Zain'] text-lg">{item.title}</h4>
                  {item.client_name && (
                    <p className="text-zinc-500 text-xs mt-1">{item.client_name}</p>
                  )}
                </div>
              </motion.div>
            </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===== PROCESS =====
function ProcessSection() {
  const steps = [
    { num: "01", title: "الاكتشاف", desc: "نفهم رؤيتك وأهدافك ونحلل السوق والمنافسين" },
    { num: "02", title: "التخطيط", desc: "نضع استراتيجية متكاملة وخطة عمل واضحة" },
    { num: "03", title: "التنفيذ", desc: "نحوّل الخطة إلى واقع بأعلى معايير الجودة" },
    { num: "04", title: "التطوير", desc: "نقيس النتائج ونطوّر باستمرار لتحقيق الأفضل" },
  ];

  return (
    <section className="py-20 md:py-28 relative overflow-hidden" data-testid="process-section">
      <div className="container mx-auto px-4 md:px-6">
        <Reveal className="text-center mb-16">
          <span className="text-primary text-sm font-semibold">كيف نعمل</span>
          <h2 className="text-3xl md:text-5xl font-black font-['Zain'] text-white mt-3">
            رحلتك معنا نحو <span className="text-gradient">النجاح</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-12 right-[12.5%] left-[12.5%] h-[1px] bg-gradient-to-l from-transparent via-primary/20 to-transparent" />

          {steps.map((step, i) => (
            <Reveal key={i} delay={i * 0.15} direction="scale">
              <div className="text-center relative" data-testid={`process-step-${i}`}>
                <div className="w-24 h-24 mx-auto rounded-2xl bg-[#131315] border border-white/5 flex items-center justify-center mb-6 relative group hover:border-primary/30 transition-all">
                  <span className="text-3xl font-black font-['Zain'] text-gradient">{step.num}</span>
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <h3 className="text-lg font-bold font-['Zain'] text-white mb-2">{step.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed max-w-[200px] mx-auto">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== PARTNER CARD =====
function PartnerCard({ partner: p, index }) {
  const [imgError, setImgError] = useState(false);
  const showLogo = p.logo_url && !imgError;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: index * 0.07, ease: [0.25, 0.46, 0.45, 0.94] }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group cursor-default"
    >
      <div className="relative p-6 rounded-2xl border border-white/[0.06] bg-[#111114] hover:border-primary/25 transition-all duration-700 flex flex-col items-center gap-3 min-w-[160px] overflow-hidden">
        {/* Glow orb behind logo on hover */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-primary/[0.06] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        {/* Top line accent */}
        <div className="absolute top-0 left-[20%] right-[20%] h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        
        <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-700 ${
          showLogo 
            ? "bg-white/[0.05] border border-white/[0.06] group-hover:bg-white/[0.1] group-hover:border-white/[0.12] group-hover:shadow-lg group-hover:shadow-primary/5" 
            : "bg-primary/[0.07] border border-primary/[0.1] group-hover:bg-primary/[0.14] group-hover:border-primary/[0.2] group-hover:shadow-lg group-hover:shadow-primary/10"
        }`}>
          {showLogo ? (
            <img
              src={p.logo_url}
              alt={p.name}
              className="h-8 w-8 object-contain opacity-50 group-hover:opacity-100 transition-all duration-700 brightness-0 invert group-hover:scale-110"
              onError={() => setImgError(true)}
            />
          ) : (
            <span className="text-primary/60 group-hover:text-primary text-xl font-black transition-all duration-700 group-hover:scale-110">{p.name?.charAt(0)}</span>
          )}
        </div>
        <span className="relative text-zinc-600 group-hover:text-zinc-200 text-xs font-bold whitespace-nowrap transition-colors duration-700 tracking-wide">{p.name}</span>
      </div>
    </motion.div>
  );
}

// ===== PARTNERS =====
function PartnersSection({ partners }) {
  if (!partners.length) return null;
  return (
    <section data-testid="partners-section" className="py-24 md:py-32 bg-[#0a0a0b] border-y border-white/5 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/[0.02] rounded-full blur-[150px]" />
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/10 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/10 to-transparent" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/20 bg-primary/[0.05] text-primary text-xs font-semibold mb-5 tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            شركاؤنا
          </span>
          <h2 className="text-3xl md:text-4xl font-black font-['Zain'] text-white mb-3">
            شركاء <span className="text-gradient">النجاح</span>
          </h2>
          <p className="text-zinc-600 text-sm max-w-sm mx-auto leading-relaxed">نفخر بثقة عملائنا وشركائنا الذين يشاركوننا رحلة التميز</p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto">
          {partners.map((p, i) => (
            <PartnerCard key={p.id} partner={p} index={i} />
          ))}
        </div>

        {/* Bottom counter */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: partners.length * 0.07 + 0.3, duration: 0.5 }}
          className="text-center mt-14"
        >
          <span className="text-zinc-700 text-xs tracking-widest">
            +{partners.length} شريك يثقون بنا
          </span>
        </motion.div>
      </div>
    </section>
  );
}

// ===== TESTIMONIALS =====
function TestimonialsSection({ testimonials }) {
  const [current, setCurrent] = useState(0);
  
  useEffect(() => {
    if (!testimonials.length) return;
    const timer = setInterval(() => {
      setCurrent(prev => (prev < testimonials.length - 1 ? prev + 1 : 0));
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  if (!testimonials.length) return null;

  const getInitials = (name) => name.split(" ").map(w => w[0]).join("").slice(0, 2);

  return (
    <section data-testid="testimonials-section" className="py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-dots opacity-20" />
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <Reveal className="text-center mb-16">
          <span className="text-primary text-sm font-semibold">آراء العملاء</span>
          <h2 className="text-3xl md:text-5xl font-black font-['Zain'] text-white mt-3">
            ماذا يقولون <span className="text-gradient">عنا</span>
          </h2>
        </Reveal>

        <div className="max-w-4xl mx-auto">
          <div className="relative bg-[#131315] border border-white/5 rounded-3xl p-8 md:p-14 overflow-hidden">
            <Quote size={80} className="absolute top-4 right-4 text-primary/[0.04]" />
            <div className="absolute top-0 right-0 w-60 h-60 bg-primary/5 rounded-full blur-[80px]" />

            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="relative z-10"
              >
                <div className="flex gap-1 mb-8">
                  {[...Array(testimonials[current]?.rating || 5)].map((_, i) => (
                    <Star key={i} size={18} className="text-primary fill-primary" />
                  ))}
                </div>
                <p className="text-white text-xl md:text-2xl leading-relaxed mb-10 font-['Zain'] font-medium">
                  "{testimonials[current]?.content}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold font-['Zain']">
                    {getInitials(testimonials[current]?.name || "")}
                  </div>
                  <div>
                    <p className="text-white font-bold font-['Zain']">{testimonials[current]?.name}</p>
                    <p className="text-zinc-500 text-sm">{testimonials[current]?.role} &mdash; {testimonials[current]?.company}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Progress dots */}
            <div className="flex items-center gap-3 mt-10">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-1 rounded-full transition-all duration-500 ${i === current ? "w-8 bg-primary" : "w-4 bg-white/10 hover:bg-white/20"}`}
                  data-testid={`testimonial-dot-${i}`}
                />
              ))}
              <div className="flex gap-2 ms-auto">
                <Button variant="ghost" size="icon" className="w-10 h-10 rounded-xl border border-white/5 text-zinc-400 hover:text-white hover:bg-white/5" onClick={() => setCurrent(prev => (prev > 0 ? prev - 1 : testimonials.length - 1))} data-testid="testimonial-prev">
                  <ChevronRight size={16} />
                </Button>
                <Button variant="ghost" size="icon" className="w-10 h-10 rounded-xl border border-white/5 text-zinc-400 hover:text-white hover:bg-white/5" onClick={() => setCurrent(prev => (prev < testimonials.length - 1 ? prev + 1 : 0))} data-testid="testimonial-next">
                  <ChevronLeft size={16} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== CTA =====
function CTASection() {
  return (
    <section data-testid="cta-section" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-[#0a0a0b]" />
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/8 rounded-full blur-[200px]" />
      
      <div className="container mx-auto px-4 md:px-6 text-center relative z-10">
        <Reveal direction="scale">
          <div className="max-w-3xl mx-auto">
            <Rocket size={40} className="text-primary mx-auto mb-6 animate-float" />
            <h2 className="text-3xl md:text-6xl font-black font-['Zain'] text-white mb-6 leading-tight">
              جاهز لصنع
              <br />
              <span className="text-gradient">قصة نجاحك؟</span>
            </h2>
            <p className="text-zinc-400 text-sm md:text-lg max-w-lg mx-auto mb-8 md:mb-12 leading-relaxed">
              لا تنتظر أكثر. تواصل معنا اليوم ودعنا نحوّل رؤيتك إلى واقع يتحدث عنه الجميع.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-[#E54D2E] text-white px-8 md:px-12 py-5 md:py-6 text-base md:text-lg rounded-xl red-glow group w-full sm:w-auto"
                  data-testid="cta-contact-btn"
                >
                  تواصل معنا الآن
                  <ArrowLeft size={18} className="ms-2 group-hover:-translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/courses">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5 px-8 md:px-12 py-5 md:py-6 text-base md:text-lg rounded-xl w-full sm:w-auto"
                  data-testid="cta-courses-btn"
                >
                  تصفح الدورات
                </Button>
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ===== MAIN =====
export default function HomePage() {
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    axios.post(`${API}/seed`).catch(() => {});
    axios.get(`${API}/portfolio`).then(r => setPortfolio(r.data)).catch(() => {});
    axios.get(`${API}/testimonials`).then(r => setTestimonials(r.data)).catch(() => {});
    axios.get(`${API}/partners`).then(r => setPartners(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <HeroSection />
      <AboutStrip />
      <ServicesSection />
      <StatsSection />
      <PortfolioPreview items={portfolio} />
      <ProcessSection />
      <PartnersSection partners={partners} />
      <FeaturedInfluencersSection />
      <TestimonialsSection testimonials={testimonials} />
      <CTASection />
    </>
  );
}
